import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { adminApi } from "../../../lib/admin";
import type { PropertyPredicateDto } from "../../../types/gigTypes";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { endOfLocalDay, parseLocalDateInput, startOfLocalDay, toIsoTimestamp } from "../../../utils/DateUtils";
import AdminFeedbackTable from "./AdminFeedbackTable";

const DEFAULT_PAGE_SIZE = 10;

const AdminFeedbackPanel = () => {
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [respondentRole, setRespondentRole] = useState("");
  const [rating, setRating] = useState("");
  const [createdFromDate, setCreatedFromDate] = useState("");
  const [createdToDate, setCreatedToDate] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);
  const debouncedSearch = useDebouncedValue(search, 300);

  const predicates = useMemo(() => {
    const nextPredicates: PropertyPredicateDto[] = [];
    if (debouncedSearch.trim().length > 0) {
      nextPredicates.push({ propertyName: "search", propertyValue: debouncedSearch.trim(), operation: "CONTAINS" });
    }
    if (type.trim().length > 0) {
      nextPredicates.push({ propertyName: "type", propertyValue: type.trim(), operation: "EQUALS" });
    }
    if (respondentRole.trim().length > 0) {
      nextPredicates.push({ propertyName: "respondentRole", propertyValue: respondentRole.trim(), operation: "EQUALS" });
    }
    if (rating.trim().length > 0) {
      nextPredicates.push({ propertyName: "rating", propertyValue: rating.trim(), operation: "EQUALS" });
    }

    const createdFrom = parseLocalDateInput(createdFromDate);
    const createdTo = parseLocalDateInput(createdToDate);
    if (createdFrom) {
      nextPredicates.push({ propertyName: "createdAt", propertyValue: toIsoTimestamp(startOfLocalDay(createdFrom)), operation: "TIMESTAMP_FROM" });
    }
    if (createdTo) {
      nextPredicates.push({ propertyName: "createdAt", propertyValue: toIsoTimestamp(endOfLocalDay(createdTo)), operation: "TIMESTAMP_TO" });
    }

    return nextPredicates;
  }, [createdFromDate, createdToDate, debouncedSearch, rating, respondentRole, type]);

  const feedbackQuery = useQuery({
    queryKey: ["admin-feedback", { page, pageSize, debouncedSearch, type, respondentRole, rating, createdFromDate, createdToDate }],
    queryFn: () =>
      adminApi.listFeedback({
        predicates,
        page: {
          number: page,
          size: pageSize,
          numberOfElements: null,
          hasNext: false,
          hasPrevious: false,
          first: false,
          last: false,
          sortBy: [{ direction: "DESC", propertyName: "createdAt" }],
        },
      }),
    placeholderData: (previousData) => previousData,
  });

  const error = feedbackQuery.error ? apiErrorToMessage(feedbackQuery.error, "Failed to load feedback.") : null;

  return (
    <div>
      {error ? <p className="mb-4 text-sm font-medium text-red-600">{error}</p> : null}
      <AdminFeedbackTable
        feedback={feedbackQuery.data?.feedback ?? []}
        isLoading={feedbackQuery.isLoading}
        search={search}
        type={type}
        respondentRole={respondentRole}
        rating={rating}
        createdFromDate={createdFromDate}
        createdToDate={createdToDate}
        pageSize={pageSize}
        page={page}
        resultsCount={feedbackQuery.data?.page.numberOfElements ?? 0}
        hasNextPage={feedbackQuery.data?.page.hasNext ?? false}
        hasPreviousPage={feedbackQuery.data?.page.hasPrevious ?? false}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(0);
        }}
        onTypeChange={(value) => {
          setType(value);
          setPage(0);
        }}
        onRespondentRoleChange={(value) => {
          setRespondentRole(value);
          setPage(0);
        }}
        onRatingChange={(value) => {
          setRating(value);
          setPage(0);
        }}
        onCreatedFromDateChange={(value) => {
          setCreatedFromDate(value);
          setPage(0);
        }}
        onCreatedToDateChange={(value) => {
          setCreatedToDate(value);
          setPage(0);
        }}
        onPageSizeChange={(value) => {
          setPageSize(value);
          setPage(0);
        }}
        onPageChange={setPage}
      />
    </div>
  );
};

export default AdminFeedbackPanel;
